import React, { useState } from 'react';
import {
    Container, Paper, TextField, Button, Typography, Box, Alert, CircularProgress, Link as MuiLink,
} from '@mui/material';
import { Link, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { apiService } from '../services/api';
import { useTranslation } from 'react-i18next';

const schema = yup.object({
    password: yup
        .string()
        .min(6, 'resetPassword.passwordMin')
        .required('resetPassword.passwordRequired'),
    confirmPassword: yup
        .string()
        .oneOf([yup.ref('password')], 'resetPassword.passwordMismatch')
        .required('resetPassword.confirmRequired'),
});

type FormData = yup.InferType<typeof schema>;

const ResetPasswordPage: React.FC = () => {
    const { t } = useTranslation();
    const [searchParams] = useSearchParams();
    const token = searchParams.get('token');

    const [isLoading, setIsLoading] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [error, setError] = useState<string>('');
    const [showPassword, setShowPassword] = useState(false);

    const {
        register,
        handleSubmit,
        formState: { errors },
    } = useForm<FormData>({ resolver: yupResolver(schema) });

    const onSubmit = async (data: FormData) => {
        if (!token) {
            setError(t('resetPassword.missingToken'));
            return;
        }
        try {
            setIsLoading(true);
            setError('');
            await apiService.resetPassword(token, data.password);
            setSubmitted(true);
        } catch (err: any) {
            const data = err.response?.data;
            let msg = data?.message || '';

            // Estrai errori di validazione dal formato ProblemDetails
            if (!msg && data?.errors) {
                const all = Object.values(data.errors).flat();
                msg = all.join('. ');
            }

            if (err.response?.status === 400) {
                setError(msg || t('resetPassword.invalidToken'));
            } else {
                setError(msg || t('resetPassword.genericError'));
            }
        } finally {
            setIsLoading(false);
        }
    };

    const togglePasswordVisibility = () => setShowPassword((prev) => !prev);

    return (
        <Container component="main" maxWidth="sm">
            <Box sx={{ marginTop: 8, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <Paper elevation={3} sx={{ padding: 4, width: '100%' }}>
                    <Typography component="h1" variant="h4" align="center" gutterBottom>
                        {t('resetPassword.title')}
                    </Typography>

                    {!token ? (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {t('resetPassword.missingToken')}
                        </Alert>
                    ) : submitted ? (
                        <Box textAlign="center">
                            <Alert severity="success" sx={{ mb: 2 }}>
                                {t('resetPassword.successMessage')}
                            </Alert>
                            <Typography variant="body2" sx={{ mt: 2 }}>
                                <MuiLink component={Link} to="/login">
                                    {t('resetPassword.backToLogin')}
                                </MuiLink>
                            </Typography>
                        </Box>
                    ) : (
                        <Box component="form" onSubmit={handleSubmit(onSubmit)} sx={{ mt: 1 }}>
                            {error && (
                                <Alert severity="error" sx={{ mb: 2 }}>
                                    {error}
                                </Alert>
                            )}

                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                {t('resetPassword.description')}
                            </Typography>

                            <TextField
                                margin="normal"
                                required
                                fullWidth
                                label={t('resetPassword.passwordLabel')}
                                type={showPassword ? 'text' : 'password'}
                                id="password"
                                autoComplete="new-password"
                                autoFocus
                                {...register('password')}
                                error={!!errors.password}
                                helperText={errors.password?.message ? t(errors.password.message as string) : ''}
                            />

                            <TextField
                                margin="normal"
                                required
                                fullWidth
                                label={t('resetPassword.confirmLabel')}
                                type={showPassword ? 'text' : 'password'}
                                id="confirmPassword"
                                autoComplete="new-password"
                                {...register('confirmPassword')}
                                error={!!errors.confirmPassword}
                                helperText={errors.confirmPassword?.message ? t(errors.confirmPassword.message as string) : ''}
                            />

                            <Button
                                type="submit"
                                fullWidth
                                variant="contained"
                                sx={{ mt: 3, mb: 2 }}
                                disabled={isLoading}
                            >
                                {isLoading ? <CircularProgress size={24} /> : t('resetPassword.resetButton')}
                            </Button>

                            <Box textAlign="center">
                                <Typography variant="body2">
                                    <MuiLink component={Link} to="/login">
                                        {t('resetPassword.backToLogin')}
                                    </MuiLink>
                                </Typography>
                            </Box>
                        </Box>
                    )}
                </Paper>
            </Box>
        </Container>
    );
};

export default ResetPasswordPage;
